import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxnZW9tZXRyaWMlMjBhcmNoaXRlY3R1cmUlMjBvZmZpY2UlMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODYyNjYyNjF8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#111827]/70" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="flex items-center gap-2 mb-4"><ShieldCheck className="h-8 w-8 text-[#2563EB]" />
            <span className="font-heading font-extrabold text-2xl">AccuSearch WPM</span></div>
          <p className="text-sm text-white/80 max-w-md leading-relaxed">
            Work process management for background address verification. Track TAT, field executives, callers and closures in one dense operations console.
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 bg-[#F9FAFB]">
        <form onSubmit={submit} className="w-full max-w-sm bg-white border border-[#E5E7EB] p-8" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <ShieldCheck className="h-6 w-6 text-[#2563EB]" />
            <span className="font-heading font-extrabold text-lg">AccuSearch WPM</span>
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-[#111827] mb-1">Sign in</h1>
          <p className="text-sm text-[#4B5563] mb-6">Use your team account to continue.</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-[#4B5563]">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 rounded-sm" placeholder="you@company.com" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-[#4B5563]">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 rounded-sm" placeholder="••••••••" data-testid="login-password-input" />
            </div>
            {error && <div className="text-sm text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2" data-testid="login-error-message">{error}</div>}
            <Button type="submit" disabled={loading}
              className="w-full rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold transition-colors duration-150"
              data-testid="login-submit-button">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
            <div className="text-right">
              <Link to="/forgot-password" className="text-xs font-semibold text-[#2563EB] hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
